import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environmentCAS';

export interface AprilGettarifRequest {
  $type: string;
  properties: {
    addresses: Array<{
      $id: string;
      type: string;
      addressLine1: string;
      postCode: string;
      city: string;
    }>;
    email: string;
  };
  persons: Array<{
    $id: string;
    birthDate: string; // "YYYY-MM-DD"
    title: string;
    lastName: string;
    birthName: string;
    firstName: string;
    birthDepartment: string;
    birthCity: string;
    nationality: string;
    politicallyExposedPerson: boolean;
    birthCountry: string;
    mandatoryScheme: string;
    professionalCategory: string;
    familyStatus: string;
    profession: string;
    acceptanceRequestPartnersAPRIL: boolean;
    acreBeneficiary: boolean;
    companyCreationDate: string; // "YYYY-MM-DD"
    swissCrossBorderWorker: boolean;
    businessCreator: boolean;
    microEntrepreneur: boolean;
    socialSecurityNumber: string;
    landlinePhone?: {
      prefix: string;
      number: string;
    };
    mobilePhone?: {
      prefix: string;
      number: string;
    };
    email: string;
  }>;
  products: Array<{
    $id: string;
    productCode: string;
    effectiveDate: string; // "YYYY-MM-DD"
    commission: string;
    termination?: {
      insurer: string;
      formerContractReference: string;
    };
    insureds: Array<{
      $id: string;
      role: string;
      person: { $ref: string };
    }>;
    coverages: Array<{
      insured: { $ref: string };
      guaranteeCode: string;
      levelCode: string;
      eligibleMadelinLaw: boolean;
    }>;
  }>;
}

// Subscription request types
export type SubscriptionMode = 'SubscriptionPaper' | 'SubscriptionOnline' | 'SubscriptionTS';
export type PaymentMethod = 'PrelevementAutomatique' | 'Cheque' | 'VirementBancaire' | 'CarteBancaire';
export type PaymentFrequency = 'Mensuelle' | 'Trimestrielle' | 'Semestrielle' | 'Annuelle' | 'Unique';

export interface AprilSubscriptionRequest {
  $type: string;
  properties?: {
    addresses?: Array<{
      $id: string;
      type: 'Actuelle' | 'Future';
      addressLine1: string;
      addressLine2?: string;
      postCode: string;
      city: string;
      countryCode?: string;
    }>;
    email: string;
    moralSubscriber?: boolean;
    effectiveDate: string;
  };
  persons: Array<{
    $id: string;
    title: string;
    lastName: string;
    firstName: string;
    birthName?: string;
    birthDate: string;
    birthDepartment?: string;
    birthCity?: string;
    nationality?: string;
    birthCountry?: string;
    mandatoryScheme?: string;
    professionalCategory?: string;
    professionalStatus?: string;
    familyStatus?: string;
    profession?: string;
    oldAgeInsuranceFunds?: string;
    numberOfDependentChildren?: number;
    creator?: boolean;
    buyer?: boolean;
    annualIncome?: number;
    annualDividends?: number;
    landlinePhone?: {
      prefix: string;
      number: string;
    };
    mobilePhone?: {
      prefix: string;
      number: string;
    };
  }>;
  companies?: Array<{
    $id: string;
    type: string;
    companyName: string;
    legalRepresentative: { $ref: string };
    address?: { $ref: string };
    siret?: string;
    landlinePhone?: {
      type: string;
      prefix: string;
      number: string;
    };
    mobilePhone?: {
      type: string;
      prefix: string;
      number: string;
    };
    idcc?: string;
    nace?: string;
    numberOfEmployees?: string;
    businessCreationDate?: string;
    datecloturebilan?: string;
    score?: string;
  }>;
  bankReferences?: Array<{
    $id: string;
    accountHolder: { $ref: string };
    accountHolderLabel?: string;
    accountsReferences: Array<{
      accountType: 'iban';
      accountNumber: string;
    }>;
    countryCode?: string;
  }>;
  termsOfPayment?: {
    accountHolder: string;
    paymentMethode: PaymentMethod;
    paymentFrequency: PaymentFrequency;
    bankReferences: { $ref: string };
    paymentDay?: number;
  };
  products: Array<{
    $id: string;
    productCode: string;
    insured: Array<{
      $id: string;
      role: 'AssurePrincipal' | 'souscripteur' | 'representantlegal' | 'enfant' | 'payeur';
      person: { $ref: string };
      company?: { $ref: string };
    }>;
    ageReached?: boolean;
    deductionRO?: boolean;
    reBuy?: boolean;
    commission?: string;
    effectiveDate: string;
    coverages?: Array<{
      guaranteeCode: string;
      deductibleCode?: string;
      levelCode?: string;
      indemnityRatio?: number;
      income?: number;
    }>;
    promotions?: Array<{
      promotionSelected: boolean;
      promotionCode: string;
      promotionValue?: string;
    }>;
  }>;
}

export interface AprilSubscriptionResponse {
  content: string;
  projectId?: string;
  marketplaceUrl?: string;
  numeros?: { [key: string]: string[] };
  documents?: Array<{
    documentId: string;
    projectId: string;
    documentType: string;
    documentTitle: string;
    documentDescription: string;
    documentLocation?: string;
  }>;
  businessMessages?: Array<{
    messageType: string;
    messageTitle: string;
    messageReference: string;
    productCode?: string;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class AprilService {
  private baseUrl = `${environment.casApiUrl}${environment.apiEndpoint}/v1/comparisons/april`;
  private subscriptionUrl = `${environment.casApiUrl}${environment.apiEndpoint}/v1/april/subscription`;

  constructor(private http: HttpClient) {}

  /**
   * POST to /projects/prices
   * - payload must follow AprilGettarifRequest schema
   * - pricingType & withSchedule as query params
   * - x-projectUuid in header
   */
  getTarif(
    payload: AprilGettarifRequest,
    pricingType: 'Simple' | 'Complete' = 'Simple',
    withSchedule: 'true' | 'false' = 'false',
    projectUuid: string
  ): Observable<any> {
    const url = `${this.baseUrl}/projects/prices`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-projectUuid': projectUuid
    });
    const params = new HttpParams()
      .set('pricingType', pricingType)
      .set('withSchedule', withSchedule);

    return this.http.post(url, payload, { headers, params });
  }

  // ==================== SUBSCRIPTION METHODS ====================

  /**
   * Workflow intelligent de souscription
   * POST /api/v1/april/subscription/smart
   * 
   * Automatise le workflow complet:
   * 1. Crée un projet avec Quotation
   * 2. Récupère les modes de souscription disponibles
   * 3. Choisit le meilleur mode disponible
   * 4. Lance la souscription
   */
  startSmartSubscription(projectRequest: any): Observable<AprilSubscriptionResponse> {
    const url = `${this.subscriptionUrl}/smart`;
    return this.http.post<AprilSubscriptionResponse>(url, projectRequest);
  }

  /**
   * Lister les modes de souscription disponibles pour un projet
   * GET /api/v1/april/subscription/modes/{projectId}?personId={personId}
   */
  getSubscriptionModes(projectId: string, personId?: string): Observable<any[]> {
    const url = `${this.subscriptionUrl}/modes/${projectId}`;
    let params = new HttpParams();
    if (personId) {
      params = params.set('personId', personId);
    }
    return this.http.get<any[]>(url, { params });
  }

  /**
   * Démarrer l'adhésion en mode signature Papier
   * POST /api/v1/april/subscription/paper
   * Génère un bulletin d'adhésion à imprimer et faire signer manuellement
   */
  startSubscriptionPaper(projectRequest: AprilSubscriptionRequest): Observable<AprilSubscriptionResponse> {
    const url = `${this.subscriptionUrl}/paper`;
    return this.http.post<AprilSubscriptionResponse>(url, projectRequest);
  }

  /**
   * Démarrer l'adhésion en mode signature En Ligne
   * POST /api/v1/april/subscription/online
   * Envoie un mail au client avec un lien vers un espace sécurisé pour signer
   */
  startSubscriptionOnline(projectRequest: AprilSubscriptionRequest): Observable<AprilSubscriptionResponse> {
    const url = `${this.subscriptionUrl}/online`;
    return this.http.post<AprilSubscriptionResponse>(url, projectRequest);
  }

  /**
   * Démarrer l'adhésion en mode Télé-sélection
   * POST /api/v1/april/subscription/teleselection
   */
  startSubscriptionTS(projectRequest: AprilSubscriptionRequest): Observable<AprilSubscriptionResponse> {
    const url = `${this.subscriptionUrl}/teleselection`;
    return this.http.post<AprilSubscriptionResponse>(url, projectRequest);
  }

  /**
   * Récupérer les documents d'un projet
   * GET /api/v1/april/subscription/{projectId}/documents
   */
  getProjectDocuments(projectId: string, personId?: string): Observable<any[]> {
    let params = new HttpParams();
    if (personId) {
      params = params.set('personId', personId);
    }
    const url = `${this.subscriptionUrl}/${projectId}/documents`;
    return this.http.get<any[]>(url, { params });
  }

  /**
   * Télécharger un document (PDF)
   * GET /api/v1/april/subscription/{projectId}/documents/{documentId}
   */
  downloadDocument(projectId: string, documentId: string, personId?: string): Observable<Blob> {
    let params = new HttpParams();
    if (personId) {
      params = params.set('personId', personId);
    }
    const url = `${this.subscriptionUrl}/${projectId}/documents/${documentId}`;
    return this.http.get(url, { params, responseType: 'blob' });
  }

  /**
   * Valider l'adhésion
   * POST /api/v1/april/subscription/{projectId}/validate
   * Retourne les numéros d'adhérent pour chaque contrat souscrit
   */
  validateSubscription(projectId: string): Observable<AprilSubscriptionResponse> {
    const url = `${this.subscriptionUrl}/${projectId}/validate`;
    return this.http.post<AprilSubscriptionResponse>(url, {});
  }

  /**
   * Annuler l'adhésion
   * POST /api/v1/april/subscription/{projectId}/cancel
   */
  cancelSubscription(projectId: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${projectId}/cancel`;
    return this.http.post(url, {});
  }

  /**
   * Récupérer les documents post-signature
   * GET /api/v1/april/subscription/{projectId}/validated-documents/{personId}
   */
  getValidatedDocuments(projectId: string, personId: string): Observable<any[]> {
    const url = `${this.subscriptionUrl}/${projectId}/validated-documents/${personId}`;
    return this.http.get<any[]>(url);
  }

  /**
   * Mettre à jour un document d'adhésion (upload signé)
   * POST /api/v1/april/subscription/{projectId}/document
   */
  updateDocument(projectId: string, personId: string, documentType: string, documentContent: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${projectId}/document`;
    const body = {
      personId,
      documentType, // DemandeAdhesion, Mandat, DossierDePreuve
      document: documentContent // Base64 encoded
    };
    return this.http.post(url, body);
  }

  /**
   * Obtenir un lot de documents
   * GET /api/v1/april/subscription/{projectId}/batch/{documentBatchId}/{personId}
   */
  getDocumentBatch(projectId: string, documentBatchId: string, personId: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${projectId}/batch/${documentBatchId}/${personId}`;
    return this.http.get(url);
  }

  /**
   * Obtenir l'URL du projet (réouverture April-On)
   * GET /api/v1/april/subscription/{projectId}/url
   */
  getProjectUrl(projectId: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${projectId}/url`;
    return this.http.get(url);
  }
}
